// @flow

import React from 'react';
import { withRouter } from 'next/router';

import { AutoNav } from '@zapier/design-system';
import { Screen } from '@zapier/theme';
import { splitUrlPiece } from '@zapier/common-utils';
import { useMediaProps } from '@zapier/common-components';

import ApolloQuery from 'web-ssr/app/common/components/ApolloQuery';
import appDirectoryNavQuery from 'web-ssr/app/app-directory/components/AppDirectoryNav/appDirectoryNavQuery';
import { navigationLinks } from 'web-ssr/app/app-directory/components/AppDirectoryNav/constants';

import type { RouterProps } from 'web-ssr/app/common/next/types';
import type {
  appDirectoryNavQuery as appDirectoryNavQueryType,
  appDirectoryNavQueryVariables,
} from 'web-ssr/graphql/types';

type Result = {
  loading: *,
  fetchMore: *,
  error: *,
  data: ?appDirectoryNavQueryType,
};

type Props = {|
  primaryAppSlug: string,
  router: RouterProps,
|};

const isExactMatchHref = (itemHref, currentHref) => {
  return itemHref === currentHref;
};

const isPartialMatchHref = (itemHref, currentHref) => {
  return currentHref.includes(itemHref);
};

const AppDirectoryNavContainer = (props: Props) => {
  const { primaryAppSlug, router } = props;

  const variables: appDirectoryNavQueryVariables = { slug: primaryAppSlug };

  const layoutProps = useMediaProps({
    [Screen.queries.phone]: { size: 'medium' },
    [Screen.queries.phablet]: { size: 'medium' },
    [Screen.queries.tablet]: { size: 'large' },
    [Screen.queries.desktop]: { size: 'large' },
  });

  return (
    <ApolloQuery query={appDirectoryNavQuery} variables={variables}>
      {(result: Result) => {
        if (!result.data || !result.data.appBySlug) {
          return null;
        }

        const { baseHref } = splitUrlPiece('?', router.asPath);

        const links = navigationLinks
          .filter(item => item.isVisible(result.data))
          .map(item => {
            const itemHref = item.getHref(primaryAppSlug);
            return {
              href: itemHref,
              text: item.text,
              isExact: item.isExact,
            };
          });

        return (
          <AutoNav
            ariaLabel="app directory content"
            isItemSelected={item => {
              /**
               * Some of the links in the App Directory navigation are
               * highlighted based on a partial match of the route. For example
               * We have tutorials and tutorial detail pages. We want the
               * highlight to appear when on either the tutorial list or details
               * pages. To do this, we partially match the `href`.
               */

              // `NavItemType`, which `AutoNav` uses internally, is a strict
              // type. Since we are attaching `isExact` to its shape, this is
              // throwing an error.
              // $FlowFixMe
              return item && item.isExact
                ? isExactMatchHref(item.href, baseHref)
                : isPartialMatchHref(item.href, baseHref);
            }}
            // See comment above
            // $FlowFixMe
            items={links}
            selectedItemHref={splitUrlPiece('?', router.asPath).baseHref}
            size={layoutProps.size}
          />
        );
      }}
    </ApolloQuery>
  );
};

export default withRouter(AppDirectoryNavContainer);
